/**
 * Google Sign-In button — Direct Google OAuth via Google Identity
 * Services (GIS, https://accounts.google.com/gsi/client).
 *
 * Renders the official Google button which, on success, hands us an
 * ID token (`credential`). We POST it to the backend, which validates
 * and returns our own JWT.
 *
 * Behaviour:
 *  - existing customer → access_token + login
 *  - existing admin     → backend 403 with `code:admin_password_login_required`
 *                         → we surface the message; admin must use password+MFA
 *  - unknown email      → backend 403 with `code:signup_required` → we
 *                         hand the verified Google profile to the
 *                         registration form (pre-filled, e-mail already
 *                         proven) instead of showing a dead-end error.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

const API_URL    = process.env.REACT_APP_BACKEND_URL;
const CLIENT_ID  = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const GIS_SRC    = 'https://accounts.google.com/gsi/client';

function loadGisOnce() {
  if (typeof window === 'undefined') return Promise.reject(new Error('no window'));
  if (window.google?.accounts?.id) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${GIS_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('GIS script failed')));
      return;
    }
    const s = document.createElement('script');
    s.src = GIS_SRC;
    s.async = true;
    s.defer = true;
    s.onload  = () => resolve();
    s.onerror = () => reject(new Error('GIS script failed'));
    document.head.appendChild(s);
  });
}

export default function GoogleSignInButton({ onSuccess, onSignupRequired, remember = false }) {
  const { t, i18n } = useTranslation('common');
  const btnRef = useRef(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleCredential = async (response) => {
    setLoading(true); setError('');
    try {
      const r = await fetch(`${API_URL}/api/auth/google/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ credential: response.credential }),
      });
      const data = await r.json();
      if (!r.ok) {
        // Backend wraps the structured detail in `detail`.
        const detail = (typeof data.detail === 'object') ? data.detail : null;
        if (detail?.code === 'signup_required') {
          // Not an error for the visitor: hand the verified Google profile
          // to the registration form so they only fill country, phone and
          // a password. No red message — the form shows a green banner.
          onSignupRequired?.({
            email:      detail.email,
            first_name: detail.first_name || '',
            surname:    detail.surname || '',
            picture:    detail.picture || null,
            credential: response.credential,
          });
          return;
        }
        if (detail?.code === 'admin_password_login_required') {
          setError('Admin accounts must sign in with email, password, and MFA. Google Sign-In is not enabled for admins.');
          return;
        }
        throw new Error((typeof data.detail === 'string' ? data.detail : detail?.message) || `HTTP ${r.status}`);
      }
      // Persist + notify parent.
      if (remember) {
        localStorage.setItem('auth_token', data.access_token);
        localStorage.setItem('user', JSON.stringify(data.user));
      } else {
        sessionStorage.setItem('auth_token', data.access_token);
        sessionStorage.setItem('user', JSON.stringify(data.user));
      }
      if (data.link_action === 'signed_up') toast.success('Welcome! Account created via Google.');
      else if (data.link_action === 'linked') toast.success('Google account linked.');
      onSuccess?.(data.user, data.access_token);
    } catch (e) {
      setError(e.message || 'Google sign-in failed');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!CLIENT_ID) {
      setError('Google Sign-In is not configured (missing REACT_APP_GOOGLE_CLIENT_ID).');
      return;
    }
    let cancelled = false;
    loadGisOnce().then(() => {
      if (cancelled || !window.google?.accounts?.id || !btnRef.current) return;
      try {
        window.google.accounts.id.initialize({
          client_id:  CLIENT_ID,
          callback:   handleCredential,
          // Allow the Authorized JavaScript origins we registered.
          // No FedCM-required scopes — we use openid email profile,
          // which GIS handles by default.
        });
        window.google.accounts.id.renderButton(btnRef.current, {
          type:  'standard',
          theme: 'outline',
          size:  'large',
          text:  'signin_with',
          shape: 'rectangular',
          logo_alignment: 'left',
          width: 320,
          // Google renders the button label itself; without this it falls
          // back to the browser locale, so an English UI could still show
          // "Вход через аккаунт Google".
          locale: (i18n.language || 'en').split('-')[0],
        });
      } catch (e) {
        setError(`Could not render Google button: ${e.message || e}`);
      }
    }).catch((e) => setError(e.message || 'Could not load Google script'));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  return (
    <div className="flex flex-col items-center gap-2" data-testid="google-signin">
      <div ref={btnRef} aria-label="Sign in with Google" />
      <p className="text-[11px] text-slate-400 text-center" data-testid="google-signin-hint">
        {t('google_signin_hint', { defaultValue: 'For existing customers — new users will continue to registration' })}
      </p>
      {loading && <div className="text-xs text-slate-500">{t('google_signin_progress', { defaultValue: 'Signing you in…' })}</div>}
      {error && <div className="text-xs text-rose-600" data-testid="google-signin-error">{error}</div>}
    </div>
  );
}
